<script>
import {defineComponent, h} from 'vue';
import {buildTree, TreeNode, ObjectValue, getTreeNodeDisplayLabel} from './inspector_tree.ts';

const STYLES = window.getComputedStyle(document.body);
const DIFF_GREEN = STYLES.getPropertyValue('--color-diff-added-word-bg');
const DIFF_YELLOW = STYLES.getPropertyValue('--color-diff-moved-row-bg');
const DIFF_RED = STYLES.getPropertyValue('--color-diff-removed-word-bg');
const DIFF_NONE = 'transparent';

const OMIT_JSON_FIELDS = [
  'not_fitted_for_variants',
  'hide_for_variants',
  'net_id',
];

/**
 * Events:
 *
 * - inspector-close: request that the inspector frame should hide
 */
export default defineComponent({
  emits: ['inspector-close'],

  data() {
    return {
      tree: null,
      key: '',
      type: '',
    };
  },
  methods: {

    /**
     * Rerender the popup with new data
     * @param {object | number | string | null} headContents
     * @param {object | number | string | null} baseContents
     * @param {string} type
     * @param {string} variant
     * @param {string} key
     * @param {boolean} isDiff
     * @param {string} units
     * @param {InspectorContext} metadataCtx
     * @param {TreeNode | null} prebuiltTree
     * @returns {void}
     */
    repopulate(headContents, baseContents, type, variant, key, isDiff, units, metadataCtx, prebuiltTree = null) {
      this.type = type;
      this.key = key;

      if (prebuiltTree !== null) {
        this.tree = this.makePrebuiltInspectorTree(prebuiltTree);
        this.$forceUpdate();
        return;
      }

      this.omitFields(headContents, variant);
      this.omitFields(baseContents, variant);

      this.tree = buildTree(
        '',
        headContents,
        baseContents,
        0,
        false,
        false,
        isDiff,
        units,
        false,
        null,
        metadataCtx);
      // rerender
      this.$forceUpdate();
    },

    makePrebuiltInspectorTree(prebuiltTree) {
      const root = new TreeNode(
        '',
        new ObjectValue(null),
        new ObjectValue(null),
        false,
        prebuiltTree.children.length === 0,
        0,
        '',
        false,
        false,
        false,
        false,
      );

      for (const child of prebuiltTree.children) {
        const clonedChild = this.cloneTreeNode(child, 1);
        if (clonedChild !== null) {
          root.addChild(clonedChild);
        }
      }

      root.empty = root.children.length === 0;

      return root;
    },

    cloneTreeNode(node, level) {
      if (OMIT_JSON_FIELDS.includes(node.key)) {
        return null;
      }

      const preservesOwnDiffState = node.primitive;

      const cloned = new TreeNode(
        node.key,
        node.head,
        node.base,
        node.primitive,
        node.empty,
        level,
        '',
        false,
        node.added,
        preservesOwnDiffState ? node.modified : false,
        node.removed,
      );

      for (const child of node.children) {
        const clonedChild = this.cloneTreeNode(child, level + 1);
        if (clonedChild !== null) {
          cloned.addChild(clonedChild);
        }
      }

      if (!cloned.primitive) {
        cloned.empty = cloned.children.length === 0;
        if (cloned.empty) {
          return null;
        }
      }

      return cloned;
    },

    /**
     * Remove fields that shouldn't be displayed
     * @param {object | number | string | null} contents
     * @param {string} variant
     */
    omitFields(contents, variant) {
      if ((contents !== null) && (Array.isArray(contents) || (typeof contents === 'object'))) {
        for (const key of Object.keys(contents)) {
          if (key !== 'variants') {
            this.omitFields(contents[key], variant);
          }

          // delete any fields that should be omitted
          if (OMIT_JSON_FIELDS.includes(key) || (contents[key] === null)) {
            delete contents[key];
          }
        }
      }
    },

    /**
     * Get a caret depending on the object
     * @param {TreeNode} node
     * @returns {VNode}
     */
    renderCaret(node) {
      if (node.children.length > 0) {
        return h('div',
          {
            class: 'caret',
            onClick: () => {
              node.collapsed = !node.collapsed;
              this.toggleCaret(node);
              this.toggleSection(node, node.collapsed);
            },
          },
          [
            h('i',
              {
                class: `caret-icon caret-icon-${node.collapsed ? 'right' : 'down'}`,
                ref: `${node.id}-caret`,
              },
              [],
            ),
          ],
        );
      }
      return h('div', {class: 'caret'}, []);
    },

    /**
     * Render the node value
     * @param {TreeNode} node
     * @returns {VNode}
     */
    renderValue(node) {
      const valueChildren = [];

      if (node.removed) {
        valueChildren.push(node.base.value({}));
      } else if (!node.added && (node.primitive && node.modified)) {
        valueChildren.push([
          node.base.value({
            'style': [
              {'background-color': DIFF_RED},
              {'display': 'inline'},
            ],
          }),
          h('div', {
            innerHTML: '&nbsp;&rarr;&nbsp;',
            'style': [
              {'display': 'inline'},
            ],
          }),
          node.head.value({
            'style': [
              {'background-color': DIFF_GREEN},
              {'display': 'inline'},
            ],
          }),
        ]);
      } else {
        valueChildren.push(node.head.value({}));
      }

      return h('div',
        {
          class: ['metadata-value'],
        },
        valueChildren);
    },

    /**
     * Toggle a node section on/off. We hide all descendants of the node
     * but only "collapse" the original node
     * @param {TreeNode} node
     * @param {boolean} parentCollapsed
     */
    toggleSection(node, parentCollapsed) {
      for (const child of node.children) {
        if (child.id !== '') {
          if (parentCollapsed) {
            this.$refs[child.id].classList.replace('metadata-expanded', 'metadata-collapsed');
          } else {
            this.$refs[child.id].classList.replace('metadata-collapsed', 'metadata-expanded');
          }

          this.toggleSection(child, parentCollapsed || child.collapsed);
        }
      }
    },

    /**
     * Rotate the caret icon for a section
     * @param {TreeNode} node
     */
    toggleCaret(node) {
      if (node.id !== '') {
        if (node.collapsed) {
          this.$refs[`${node.id}-caret`].classList.replace('caret-icon-down', 'caret-icon-right');
        } else {
          this.$refs[`${node.id}-caret`].classList.replace('caret-icon-right', 'caret-icon-down');
        }
      }
    },

    /**
     * Traverse the tree, call handler on each node, return array of results
     * @param {TreeNode} tree
     * @param {(TreeNode) => VNode} handler
     * @returns Array<VNode>
     */
    traverseTree(tree, handler) {
      const results = [];

      // don't render empty trees
      if (tree.empty) {
        return results;
      }

      // don't render root object or type/unique id at first level (these are shown in the title)
      if ((tree.level > 1) || ((tree.level === 1) && (tree.key !== 'type') && (tree.key !== 'unique_id'))) {
        results.push(handler(tree));
      }

      if (tree.children.length > 0) {
        for (const child of tree.children) {
          results.push(this.traverseTree(child, handler));
        }
      }

      return results;
    },

    /**
     *
     * @returns {VNode}
     */
    renderTree() {
      let elemCount = 0;

      return h(
        'div',
        {
          class: 'metadata-container',
          key: this.key,
        },
        this.traverseTree(this.tree, (node) => {
          let diffColor = DIFF_NONE;

          if (node.added) {
            diffColor = DIFF_GREEN;
          } else if (node.removed) {
            diffColor = DIFF_RED;
          } else if (node.modified && !node.primitive) {
            diffColor = DIFF_YELLOW;
          }

          // the id to use as a ref for toggling a section
          elemCount++;
          node.id = elemCount.toString();

          const children = [
            this.renderCaret(
              node,
            ),
            h('div',
              {class: 'metadata-key'},
              getTreeNodeDisplayLabel(node.key, node)),
            h('div',
              {class: 'metadata-sep'},
              ':'),
          ];

          if (node.children.length > 0) {
            children.push(
              h('div',
                {class: 'metadata-len'}, `(${node.children.length})`,
              ),
            );
          }

          return h('div',
            {
              class: ['metadata-line', ((node.level > 1) ? 'metadata-collapsed' : 'metadata-expanded')],
              ref: elemCount.toString(),
              style: [
                {'margin-left': `${(node.level - 1) * 18}px`},
                {'background-color': diffColor},
              ],
            },
            children.concat(
              this.renderValue(
                node,
              ),
            ),
          );
        }),
      );
    },

    /**
     * Render metadata based on current contents
     * @returns {VNode}
     */
    renderCloseButton() {
      return h('a', {
        class: 'metadata-close-button',
        onClick: (e) => {
          this.$emit('inspector-close', e);
        },
        innerHTML: '<svg viewBox="0 0 15 15" class="svg octicon-x" width="15" height="15" aria-hidden="true">' +
          '<path fill-rule="evenodd" d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06z"/>' +
          '</svg>',
      });
    },
  },

  /**
   * Render metadata based on current contents
   * @returns {VNode}
   */
  render() {
    if (this.tree !== null) {
      const displayKey = getTreeNodeDisplayLabel(this.key, this.tree);
      const shouldShowKey = displayKey !== '' && (displayKey !== this.key || displayKey.length <= 10);

      if (this.type === '') {
        return this.renderTree();
      }

      // only show the type if the unique id is too long or not available
      if (!shouldShowKey) {
        return h('div', [
          h('div', {class: 'metadata-title'}, [
            this.type,
            this.renderCloseButton(),
          ]),
          this.renderTree(),
        ]);
      }

      return h('div', [
        h('div', {class: 'metadata-title'}, [
          this.type,
          h('div', {class: 'metadata-len'}, `(${displayKey})`),
          this.renderCloseButton(),
        ]),
        this.renderTree(),
      ]);
    }
    return h('div', 'empty');
  },
});
</script>

<style scoped>

.metadata-container {
  font-size: 16px;
  background-color: var(--color-box-body);
  color: var(--color-text);
  box-sizing: border-box;
  overflow: auto;
  max-height: 500px;
}

.metadata-line {
  margin: 4px 0;
  display: flex;
  justify-content: flex-start;
}

.caret {
  width: 18px;
  text-align: center;
  cursor: pointer;
}

.caret-icon {
  display: inline-block;
  border-style: solid;
  width: 0;
  height: 0;
}

.caret-icon-down {
  border-width: 6px 5px 0;
  border-color: var(--color-text) transparent;
}

.caret-icon-right {
  border-width: 5px 0 5px 6px;
  border-color: transparent transparent transparent var(--color-text);
}

.metadata-collapsed {
  display: none;
}

.metadata-expanded {
  display: flex;
}

.metadata-key {
  color: var(--color-text);
  margin-right: 4px;
  margin-left: 4px;
}

.metadata-value {
  margin-left: 8px;
}

.metadata-len {
  margin-right: 4px;
  margin-left: 4px;
  color: var(--color-text-light-3);
  font-size: 14px;
  user-select: none;
}

.metadata-title {
  padding-top: 5px;
  padding-bottom: 3px;
  display: flex;
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  background-color: var(--color-box-body);
  color: var(--color-text);
  margin-left: 4px;
  border-bottom: 2px solid var(--color-text-light-3);
}

.metadata-close-button {
  padding: 5px 10px 10px;
  display: flex;
  margin-left: auto;
  cursor: pointer;
  user-select: none;
  color: var(--color-text);
}

</style>
